import {
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { concat } from 'lodash';
import type {
  FetchReturn,
  IComponent,
  PaginationResponse,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapAllComponents } from './helpers';

export const useGetComponents = (
  filters: string[],
  overrideQuerySetting?: UseQueryOptions<IComponent[]>,
) => {
  return useQuery({
    queryKey: queryKeys.components.all(filters),
    queryFn: () => fetchComponents(concat(filters, 'pageSize=0')),

    ...overrideQuerySetting,
  });
};

export const useGetComponentsWithoutInclude = (
  filters: string[],
  overrideQuerySetting?: UseQueryOptions<IComponent[]>,
) => {
  return useQuery({
    queryKey: queryKeys.components.all(filters),
    queryFn: () => fetchComponentsWithoutInclude(concat(filters, 'pageSize=0')),

    ...overrideQuerySetting,
  });
};

export const useGetComponentsWithPagination = (
  filters: string[],
  overrideQuerySetting?: UseInfiniteQueryOptions<
    PaginationResponse<IComponent[]>
  >,
) => {
  return useInfiniteQuery({
    queryKey: queryKeys.components.pagination(filters),
    queryFn: ({ pageParam = 1 }) =>
      fetchComponentList(concat(filters, [`page=${pageParam}`]), true),

    getNextPageParam: (lastPage, allPages) => {
      const currentPage = lastPage ? allPages.length : 1;
      const totalPages = lastPage ? lastPage.totalPages : 1;

      return currentPage < totalPages ? currentPage + 1 : undefined;
    },

    ...overrideQuerySetting,
  });
};

export const fetchComponentList = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IComponent[]>> => {
  const response = await requestCCApi({
    url: `/componentListing${generateQuery(
      [],
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  if (isPagination) {
    return {
      data: response.data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IComponent[]>;
  }

  return response.data as FetchReturn<T, IComponent[]>;
};

export const fetchComponentsWithoutInclude = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
): Promise<FetchReturn<T, IComponent[]>> => {
  const response = await requestCCApi({
    url: `/components${generateQuery(
      [],
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  const data = mapAllComponents(response.data, response.includes);

  if (isPagination) {
    return {
      data: data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IComponent[]>;
  }

  return data as FetchReturn<T, IComponent[]>;
};

export const useGetComponentsWithIncludes = (
  filters: string[],
  includes: string[],
  overrideQuerySetting?: UseQueryOptions<IComponent[]>,
) => {
  return useQuery({
    queryKey: queryKeys.components.all([
      ...filters,
      `include=${includes.join(',')}`,
    ]),
    queryFn: () =>
      fetchComponents(concat(filters, 'pageSize=0'), false, includes),
    ...overrideQuerySetting,
  });
};

const DEFAULT_COMPONENT_INCLUDES = [
  'componentTypes',
  'layoutTypes',
  'productCategories',
  'productVariations',
  'dayparts',
  'componentTags',
  'assets',
  'themes',
  'phases',
];

export const SCHEDULE_COMPONENT_INCLUDES = ['componentTypes', 'themes'];

export const fetchComponents = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
  includes: string[] = DEFAULT_COMPONENT_INCLUDES,
): Promise<FetchReturn<T, IComponent[]>> => {
  const response = await requestCCApi({
    url: `/components${generateQuery(
      includes,
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  const data = mapAllComponents(response.data, response.includes);

  if (isPagination) {
    return {
      data: data,
      totalPages: response.totalPages ?? 0,
      totalCount: response.totalCount ?? 0,
    } as FetchReturn<T, IComponent[]>;
  }

  return data as FetchReturn<T, IComponent[]>;
};
